import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import DriveEtaIcon from '@mui/icons-material/DriveEta';

export default function HertzAutoPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setError('Please select a CSV file');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError(null);
      setSuccess(false);
    }
  };

  const handleProcess = async () => {
    if (!selectedFile) {
      setError('Please select a CSV file first');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch('/api/hertz/process-csv', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to process CSV');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'processed_vins.csv';
      a.click();
      window.URL.revokeObjectURL(url);

      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setError(null);
    setSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DriveEtaIcon fontSize="large" />
          Hertz Auto - VIN Processor
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload a CSV file containing VINs to process through the Hertz Lambda
        </Typography>
      </Box>

      <Card sx={{ mb: 3, maxWidth: 800 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Process VINs
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a CSV file with VINs, then click Process to receive the enriched output.
          </Typography>

          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
            Supported format: CSV file with a column containing VINs.
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                disabled={isProcessing}
              >
                Choose CSV
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {selectedFile && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleClear}
                    disabled={isProcessing}
                  >
                    Clear
                  </Button>
                </>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleProcess}
                disabled={!selectedFile || isProcessing}
                startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <DriveEtaIcon />}
                sx={{
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                }}
              >
                {isProcessing ? 'Processing...' : 'Process & Download'}
              </Button>
              {isProcessing && (
                <Typography variant="body2" color="text.secondary">
                  This may take a minute...
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {success && (
        <Card sx={{ maxWidth: 800, backgroundColor: '#f1f8f4' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" />
              <Typography variant="h6" color="success.main">
                Download complete. Your processed CSV has been saved.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {error && (
        <Card sx={{ maxWidth: 800, backgroundColor: '#fff4f4' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ErrorIcon color="error" />
              <Typography variant="h6" color="error">
                Processing Failed
              </Typography>
            </Box>
            <Paper sx={{ p: 2, backgroundColor: '#ffebee' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                {error}
              </Typography>
            </Paper>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
